// 職責：TanStack Query 的全域設定 —— 快取分層、重試策略，以及「切店／登出必須清空」的單一入口

/*
 * 為什麼快取策略被當成一級設定而不是各頁自便（doc 06 §5.3）：
 *   後端沒有分頁，每個 list 端點都回整張表。一次多餘的重取＝重新下載整張餐品表，
 *   在千筆規模下是秒級的可感延遲。因此 staleTime 在這裡是效能參數，不是體驗微調。
 *
 * 為什麼 retry 一律關閉：
 *   重試邏輯已經在 api/http.ts 收斂（它才知道哪些 ApiErrorKind 值得重試、哪些是
 *   會產生副作用的寫入）。Query 層再重試一次，等於同一個失敗被放大成 N 次請求，
 *   而且 http 層的 requestId 會對不上使用者回報的那一筆。
 */

import {
  MutationCache,
  QueryClient,
  type QueryClientConfig,
  type VueQueryPluginOptions,
} from '@tanstack/vue-query'

import { ApiError } from '@/api/http'
import { useUiStore } from '@/stores/ui'

/**
 * 依資料性質分層的 staleTime（毫秒），對應 doc 06 §5.3 的表格。
 * ★ 各 composable 一律引用此處常數，不要就地寫數字 ——
 *   數字散落之後，「為什麼餐品是 60 秒」這個決策就沒有地方可讀了。
 */
export const STALE_TIME = {
  /** 主資料：分類、打印機、出品部門、字典、付款方式 —— 極少變動 */
  master: 5 * 60_000,
  /** 餐品清單：可能上千筆，避免頻繁重拉 */
  goods: 60_000,
  /** 時段、套餐、優惠 */
  catalog: 60_000,
  /** 概覽 KPI：短快取 + 視窗聚焦時重取，讓經理看到的是「剛才」的數字 */
  dashboard: 30_000,
  /** 報表：使用者主動查詢且參數多變，每次都必須是新的 */
  report: 0,
} as const

/** 快取保留時間：資料不再被任何元件使用後，再放多久才回收 */
const GC_TIME = 5 * 60_000

/**
 * 全域預設。刻意取「保守但不至於每次切頁都重拉」的中間值：
 * 真正重要的分層由各 resource 的 composable 用 STALE_TIME 覆寫。
 */
export const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME.catalog,
      gcTime: GC_TIME,
      retry: false,
      // 後端每次回整張表，聚焦即重取的成本太高；需要即時性的查詢（概覽 KPI）自行開啟
      refetchOnWindowFocus: false,
      // 斷線重連後重取是划算的：離線期間的資料幾乎必然已過時
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
}

/* ────────────────────────────── 403 呈現 ────────────────────────────── */

/**
 * 這三個 controller 的 403 是 webapi 的 **OpsManaged 硬閘**（plan §7.1，無開關）：
 * ops-console 開設的帳號／`ops$` 角色／`shopmgr-*` 功能列，門店側一律不准修改。
 * 伺服器訊息本身會說明，但補一句「到哪裡修改」讓人不需要再問。
 */
const OPS_MANAGED_ENDPOINT = /admin_employee|merchant_role|merchant_function/

export function describeForbidden(error: ApiError): string {
  const hint = OPS_MANAGED_ENDPOINT.test(error.endpoint)
    ? '此帳號／角色由 ops-console 管理，請到 ops-console 修改。'
    : ''
  return hint === '' ? error.message : `${error.message}（${hint}）`
}

/**
 * 全域 MutationCache：後端 403 一律 toast 伺服器訊息。
 *
 * 為什麼在這裡而不是逐個 view 處理：
 *   · 前端閘（`requirePermission`）與後端閘（`[RequirePermission]` / OpsManaged）讀同一批店碼，
 *     正常情況下前端已經隱藏了按鈕，到了後端才 403 代表**兩邊不同步**（目錄版本、未 resync、
 *     或者有人用 devtools 強行操作）—— 這種情況要用一句醒目的話說出來，而不是散落在各 view 的
 *     error banner；
 *   · view 本身的 `mutation.error` 照舊會顯示（這裡不吞掉錯誤），toast 只是多加一層。
 * ★ 403 **不登出**：登出是 401（token 過期）的處置；403 是「你的身分沒錯，但這件事不准做」，
 *   登出只會令人以為 session 壞了。
 */
function onMutationError(error: unknown): void {
  if (!(error instanceof ApiError) || error.kind !== 'forbidden') return
  useUiStore().toast('error', describeForbidden(error))
}

/** main.ts 安裝 VueQueryPlugin 時直接使用，確保 app 與測試共用同一份設定 */
export const queryClientOptions: VueQueryPluginOptions = { queryClientConfig }

/** 建立設定好的 QueryClient：app（main.ts）、單元測試與非 Vue 情境共用同一個入口 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    ...queryClientConfig,
    mutationCache: new MutationCache({ onError: onMutationError }),
  })
}

/**
 * ★ 切換門店與登出時必須呼叫（doc 06 §5.4 / doc 09 §3.5）。
 *
 * 為什麼是 `clear()` 而不是 `invalidateQueries()`：
 *   切店等於換一個後端資料庫。invalidate 只是標記過期，舊店的資料仍會先渲染出來
 *   再被新資料替換 —— 那一瞬間畫面上是「新店名 + 舊店餐品」，比錯誤更危險。
 *   clear() 直接丟棄，讓每個頁面回到載入態。
 *
 * ⚠️ query key 雖然都含 mid（key 天然隔離），仍必須 clear：
 *    key 隔離只防止資料互串，不會回收上一間店的記憶體，也不會處理無 mid 的全域查詢。
 */
export function resetServerState(queryClient: QueryClient): void {
  queryClient.cancelQueries()
  queryClient.clear()
}
